import {
	QueryKey,
	useMutation,
	UseMutationResult,
	useQueryClient,
} from '@tanstack/react-query'

/**
 * Descriptor for a plain trip-resource mutation (create / update / delete).
 *
 * Collapses the repeated `useQueryClient` + `useMutation` + `onSuccess`-invalidate
 * boilerplate that every plain CRUD hook used to hand-roll. The call site supplies
 * only what actually varies — the request (`mutationFn`) and which cache keys to
 * refetch (`invalidate`, derived from the mutation variables). Keys come from
 * `@lib/queryKeys` so cascade invalidation (e.g. expenses + paymentsOwed) lives in
 * one place.
 *
 * Hooks built from this keep their exact exported name, params, and return type:
 * `createTripMutation` is generic over the request payload (`TData`) and the
 * variables object (`TVars`), so the resulting `UseMutationResult` is byte-identical
 * to the previous hand-written `useMutation<TData, Error, TVars>`.
 */
interface TripMutationDescriptor<TData, TVars> {
	mutationFn: (variables: TVars) => Promise<TData>
	/** Cache keys to invalidate on success, derived from the mutation variables. */
	invalidate: (variables: TVars) => QueryKey[]
}

export function createTripMutation<TData, TVars>(
	descriptor: TripMutationDescriptor<TData, TVars>,
): () => UseMutationResult<TData, Error, TVars> {
	return () => {
		const queryClient = useQueryClient()

		return useMutation<TData, Error, TVars>({
			mutationFn: descriptor.mutationFn,
			onSuccess: (_data, variables) => {
				for (const queryKey of descriptor.invalidate(variables)) {
					queryClient.invalidateQueries({ queryKey })
				}
			},
		})
	}
}
